import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "../../components/layouts/Grid";
import { Card } from "../../components/layouts/Cards";
import { motion } from "framer-motion";
import {
  setSelectedCategory,
  setCurrentResponseIndex,
  selectCivicPulseState,
  selectActiveFilters,
} from "../../store/civicPulseSlice";
import FilterPanel from "./components/FilterPanel";
import ResponsesPanel from "./components/ResponsesPanel";
import EKGPulse from "./components/EKGPulse";
import QuestionSection from "./components/QuestionSection";
import { questionConfig } from "./config/questionConfig";
import { fetchOpenEndedData } from "./services/api";

const CivicPulseTopline = () => {
  const dispatch = useDispatch();
  const { selectedCategory, activeQuestionId, currentResponseIndex } =
    useSelector(selectCivicPulseState);

  const activeFilters = useSelector(selectActiveFilters);
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [isResponsesLoading, setIsResponsesLoading] = useState(false);
  const [isTreemapVisible, setIsTreemapVisible] = useState(false);
  const [activeQuestionTitle, setActiveQuestionTitle] = useState("");
  const [currentSeed, setCurrentSeed] = useState(1);

  const fetchResponses = useCallback(
    async (seed = currentSeed) => {
      if (selectedCategory && activeQuestionId) {
        setIsResponsesLoading(true);

        try {
          const data = await fetchOpenEndedData(
            activeQuestionId,
            selectedCategory.category,
            {
              demographics: activeFilters.demographics,
              dateFilter: activeFilters.dateFilter,
            },
            10,
            seed
          );
          setSelectedResponses(data && data[0] ? data[0].responses : []);
          dispatch(setCurrentResponseIndex(0)); // Reset to first response
        } catch (error) {
          console.error("Error fetching responses:", error);
        } finally {
          setIsResponsesLoading(false);
        }
      } else {
        setSelectedResponses([]);
      }
    },
    [selectedCategory, activeQuestionId, activeFilters, dispatch, currentSeed]
  );

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // Reset the selected category when the active filters change
  useEffect(() => {
    dispatch(setSelectedCategory(null));
  }, [activeFilters, dispatch]);

  const handleRefresh = () => {
    // Generate a random number between 1 and 100
    const newSeed = Math.floor(Math.random() * 100) + 1;
    setCurrentSeed(newSeed);
    fetchResponses(newSeed);
  };

  const handlePrevResponse = () => {
    dispatch(
      setCurrentResponseIndex(
        currentResponseIndex > 0
          ? currentResponseIndex - 1
          : selectedResponses.length - 1
      )
    );
  };

  const handleNextResponse = () => {
    dispatch(
      setCurrentResponseIndex(
        currentResponseIndex < selectedResponses.length - 1
          ? currentResponseIndex + 1
          : 0
      )
    );
  };

  const onCategoryClick = useCallback(
    (category, questionId, questionTitle) => {
      dispatch(
        setSelectedCategory({
          questionId,
          category,
        })
      );
      setActiveQuestionTitle(questionTitle);
    },
    [dispatch]
  );

  const onVisibilityChange = useCallback((isVisible) => {
    setIsTreemapVisible(isVisible);
  }, []);

  return (
    <div className="flex min-h-screen bg-style-bg">
      {/* Left Sidebar */}
      <aside className="w-72 flex-shrink-0 fixed left-0 top-0 h-screen bg-style-bg border-r border-slate-200 overflow-y-auto z-20">
        <FilterPanel />
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 ml-72 mr-0 lg:mr-96 overflow-auto relative z-10">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-style-bg pb-6 border-b border-slate-200 mb-6">
          <div className="px-6 pt-8">
            <Card className="mb-0">
              <div className="flex flex-col">
                <h1 className="!text-6xl font-extrabold text-left">
                  Civic Pulse
                </h1>
                <div className="relative w-full max-w-2xl h-[90px] sm:max-w-3xl md:max-w-4xl">
                  <div className="absolute -top-10 left-0 right-0">
                    <EKGPulse />
                  </div>
                  <motion.p
                    className="font-inter italic text-slate-600 absolute right-0 bottom-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.5, duration: 1 }}
                  >
                    Taking the pulse of our community's well-being
                  </motion.p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Main Content Grid */}
        <Grid className="px-6 pt-2">
          {/* Question Sections */}
          {questionConfig.sections.map((section) => (
            <QuestionSection
              key={section.id}
              section={section}
              onCategoryClick={onCategoryClick}
              onVisibilityChange={onVisibilityChange}
              activeFilters={activeFilters}
            />
          ))}
        </Grid>
      </main>

      {/* Right Sidebar */}
      <aside className="w-96 flex-shrink-0 fixed right-0 top-0 h-screen bg-style-bg border-l border-slate-200 overflow-y-auto z-30">
        <ResponsesPanel
          isVisible={isTreemapVisible}
          selectedCategory={selectedCategory?.category}
          selectedResponses={selectedResponses}
          currentResponseIndex={currentResponseIndex}
          onPrevResponse={handlePrevResponse}
          onNextResponse={handleNextResponse}
          onRefresh={handleRefresh}
          isLoading={isResponsesLoading}
          questionTitle={activeQuestionTitle}
        />
      </aside>
    </div>
  );
};

export { CivicPulseTopline };
